import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useOwner } from '../../contexts/OwnerContext'
import NoDataScreen from '../../components/NoDataScreen'
import PeriodFilter from '../../components/PeriodFilter'
import { useGetDashboardOverviewQuery } from '../../services/api'
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Line,
  PieChart,
  Pie,
  Cell,
  ComposedChart,
  Area
} from 'recharts'
import {
  Truck,
  FileText,
  Building2,
  DollarSign,
  Users,
  AlertTriangle,
  CheckCircle,
  Activity,
  Zap,
  Target,
  PieChart as PieChartIcon,
  LineChart,
  Bell,
  Package,
  BarChart3,
  Warehouse,
  Network,
  UserCheck,
  Clock,
  AlertCircle
} from 'lucide-react'
import { MonthRange } from '../../services/api'
import Page from '../../components/Page'
import { KPICard } from '../../components/cards'
import ChartCard from '../../components/charts/ChartCard'
import { useModuleConfig } from '../../contexts/ModuleConfigContext'
import TransportAnalysisSection from './TransportAnalysisSection'
import NFeAnalyticsSection from './NFeAnalyticsSection'
import MainSectionCard from './MainSectionCard'
import Modal from '../../components/Modal'
import { RevenueModal } from '../../components/modals/RevenueModal'
import { CTEModal } from '../../components/modals/CTEModal'
import { NFEModal } from '../../components/modals/NFEModal'
import { CDModal, ClientModal, EfficiencyModal } from '../../components/modals/OtherModals'
import ClickableCardModal from '../../components/ClickableCardModal'
import LogisticsNetworkModal from '../../components/LogisticsNetworkModal'
import ClientPortfolioModal from '../../components/ClientPortfolioModal'

const ExecutiveDashboard: React.FC = () => {
  const { isOwnerValid, isLoading: ownerLoading } = useOwner()
  const { isModuleEnabled } = useModuleConfig()

  // Estados dos modais
  const [openModal, setOpenModal] = useState<string | null>(null)
  const [isLogisticsModalOpen, setIsLogisticsModalOpen] = useState(false)
  const [isClientModalOpen, setIsClientModalOpen] = useState(false)

  // Estados do filtro de período
  const [currentYear, setCurrentYear] = useState<number>(new Date().getFullYear())
  const [currentMonths, setCurrentMonths] = useState<number[]>([])

  // Estados para Logistics Modal com período
  const [logisticsYear, setLogisticsYear] = useState<number>(new Date().getFullYear())
  const [logisticsMonths, setLogisticsMonths] = useState<number[]>([])
  const [isLoadingLogistics, setIsLoadingLogistics] = useState(false)

  // Função para calcular filtro padrão (últimos 3 meses para análise adequada)
  const getDefaultFilter = () => {
    const now = new Date()
    const currentMonth = now.getMonth() + 1 // getMonth() retorna 0-11
    const startMonth = Math.max(1, currentMonth - 2) // 3 meses atrás

    return {
      year: now.getFullYear(),
      monthRanges: [{ startMonth: startMonth, endMonth: currentMonth }] as MonthRange[]
    }
  }

  // Inicializar com filtro padrão
  useEffect(() => {
    if (isOwnerValid && !ownerLoading && currentMonths.length === 0) {
      const defaultFilter = getDefaultFilter()
      const monthsList: number[] = []
      defaultFilter.monthRanges.forEach(range => {
        for (let month = range.startMonth; month <= range.endMonth; month++) {
          if (!monthsList.includes(month)) {
            monthsList.push(month)
          }
        }
      })
      console.log('📅 Definindo período padrão:', defaultFilter.year, monthsList)
      setCurrentYear(defaultFilter.year)
      setCurrentMonths(monthsList)
    }
  }, [isOwnerValid, ownerLoading, currentMonths.length])

  // RTK Query hook with cache - só executa quando temos owner válido e parâmetros definidos
  const {
    data: dashboardResponse,
    isLoading: loading,
    error: queryError,
    refetch,
    isFetching
  } = useGetDashboardOverviewQuery(
    currentMonths.length > 0 ? { year: currentYear, months: currentMonths } : undefined,
    {
      skip: !isOwnerValid || ownerLoading || currentMonths.length === 0,
      // Cache por 5 minutos
      pollingInterval: 0,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: false,
      refetchOnReconnect: false,
    }
  )

  const dashboardData = dashboardResponse
  const error = queryError ? 'Erro ao carregar dados do dashboard' : null

  // Log para debug
  useEffect(() => {
    if (dashboardResponse) {
      console.log('📊 Dados do dashboard recebidos:', {
        hasLogisticsAnalytics: !!dashboardResponse.logistics_analytics,
        hasRecommendations: !!dashboardResponse.recommendations,
        logisticsAnalyticsKeys: dashboardResponse.logistics_analytics ? Object.keys(dashboardResponse.logistics_analytics) : [],
        ctePerformanceKeys: dashboardResponse.logistics_analytics?.cte_performance ? Object.keys(dashboardResponse.logistics_analytics.cte_performance) : [],
        recommendationsLength: dashboardResponse.recommendations?.length || 0
      })
    }
  }, [dashboardResponse])

  // Função auxiliar para formatação segura de números
  const formatNumber = (value: number | undefined | null): string => {
    if (value === undefined || value === null || isNaN(value)) {
      return '0'
    }
    return value.toLocaleString('pt-BR')
  }

  // Função para abrir modal
  const handleOpenModal = (modalType: string) => {
    setOpenModal(modalType)
  }

  // Função para fechar modal
  const handleCloseModal = () => {
    setOpenModal(null)
  }

  const handlePeriodFilter = (year: number, monthRanges: MonthRange[]) => {
    // Atualizar os parâmetros irá automaticamente disparar nova query ou usar cache
    setCurrentYear(year)

    // Converter MonthRange[] para number[] (expandir ranges)
    const monthsList: number[] = []
    monthRanges.forEach(range => {
      for (let month = range.startMonth; month <= range.endMonth; month++) {
        if (!monthsList.includes(month)) {
          monthsList.push(month)
        }
      }
    })

    setCurrentMonths(monthsList)
  }

  // Handler para mudança de período na Malha Logística
  const handleLogisticsPeriodChange = async (year: number, monthRanges: MonthRange[]) => {
    setIsLoadingLogistics(true)
    setLogisticsYear(year)
    setCurrentYear(year)

    const months = monthRanges.flatMap(range => {
      const result: number[] = []
      for (let month = range.startMonth; month <= range.endMonth; month++) {
        if (!result.includes(month)) result.push(month)
      }
      return result
    })

    setLogisticsMonths(months)
    setCurrentMonths(months)
    // O dashboard overview refetch automaticamente via RTK Query com novos parâmetros
    setIsLoadingLogistics(false)
  }

  if (loading) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center transition-colors duration-300"
        style={{ backgroundColor: 'var(--bg-primary)' }}
      >
        <div className="text-center">
          <div className="relative">
            <div 
              className="w-20 h-20 border-4 rounded-full animate-spin"
              style={{ 
                borderColor: 'var(--border-primary)', 
                borderTopColor: '#3b82f6' 
              }}
            ></div>
            <div 
              className="absolute inset-0 w-20 h-20 border-4 border-transparent rounded-full animate-spin"
              style={{ 
                borderTopColor: '#1d4ed8',
                animationDirection: 'reverse', 
                animationDuration: '1.5s' 
              }}
            ></div>
          </div>
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-2xl font-bold mt-8 mb-4"
            style={{ color: 'var(--text-primary)' }}
          >
            Carregando Dashboard Executivo
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            style={{ color: 'var(--text-secondary)' }}
          >
            Preparando insights estratégicos...
          </motion.p>
        </div>
      </div>
    )
  }


  if (!dashboardData) return null

  const {
    kpis = {},
    revenue_analytics = {},
    cte_analytics = {},
    nfe_analytics = {},
    cd_analytics = {},
    logistics_analytics = {},
    logistics_network_modal_data = {},
    client_analytics = {},
    transport_analysis = {},
    performance_metrics = {},
    strategic_insights = {},
    alerts = [],
    company_info = {}
  } = dashboardData

  // Verificar se há dados válidos para exibir (sem dados fictícios)
  const hasValidData = () => {
    // Verificar se há KPIs com valores válidos e não zerados
    const kpiValues = Object.entries(kpis || {})
    const hasKPIs = kpiValues.length > 0 && kpiValues.some(([, value]) => {
      const isValid = value !== null && value !== undefined && value !== 0
      return isValid
    })

    // Verificar se há dados de CTE reais
    const hasCTE = cte_analytics &&
      ((cte_analytics.status_distribution?.completed || 0) > 0 ||
       (cte_analytics.monthly_volume?.length || 0) > 0)

    // Verificar se há dados de NFe reais
    const hasNFe = nfe_analytics &&
      ((nfe_analytics.status_distribution?.authorized || 0) > 0)

    // Verificar se há dados de clientes reais
    const hasClients = client_analytics &&
      ((client_analytics as any)?.portfolio_health?.total_clients || 0) > 0

    // Verificar se há dados de logística reais
    const hasLogistics = logistics_analytics &&
      ((logistics_analytics as any)?.network_coverage?.total_cities || 0) > 0

    // Verificar se há PELO MENOS dados da empresa OU algum KPI válido
    const hasCompanyInfo = company_info && (company_info.id || company_info.name)

    // Verificar se há dados analíticos específicos da empresa
    const hasAnalyticsData = hasCTE || hasNFe || hasClients || hasLogistics || hasKPIs

    // Considerar válido se tiver informações da empresa OU algum dado analítico
    const result = hasCompanyInfo || hasAnalyticsData

    return result
  }

  // Se há erro ou não há dados válidos, mostrar tela elegante
  if (error || (!loading && !hasValidData())) {
    return (
      <NoDataScreen
        companyName={company_info?.name || "Empresa"}
        onRetry={() => refetch()}
        isLoading={loading}
      />
    )
  }

  // Função para filtrar KPIs baseado nas configurações dos módulos
  const getFilteredKPIs = () => {
    const allKPIs = [
      { 
        key: 'total_revenue', 
        label: 'Receita Total', 
        value: `R$ ${((kpis.total_revenue || 0) / 1000000).toFixed(1)}M`, 
        icon: DollarSign, 
        color: 'green' as const, 
        change: { value: `+${(kpis.monthly_growth || 0).toFixed(1)}%`, type: 'increase' as const },
        module: null // Sempre visível
      },
      {
        key: 'total_ctes',
        label: 'CTEs Totais',
        value: formatNumber(kpis.total_ctes || 0),
        icon: Truck,
        color: 'blue' as const,
        module: 'cte'
      },
      {
        key: 'total_nfes',
        label: 'NFes Processadas',
        value: formatNumber(kpis.total_nfes || 0),
        icon: FileText,
        color: 'purple' as const,
        module: 'nfe'
      },
      {
        key: 'active_cds',
        label: 'CDs Ativos',
        value: (kpis.active_cds || 0).toString(),
        icon: Building2,
        color: 'orange' as const,
        module: 'cd'
      },
      {
        key: 'total_clients',
        label: 'Clientes',
        value: (kpis.total_clients || 0).toString(),
        icon: Users,
        color: 'pink' as const,
        module: 'clients'
      },
      {
        key: 'efficiency_score',
        label: 'Eficiência',
        value: `${(kpis.efficiency_score || 0).toFixed(1)}%`,
        icon: Target,
        color: 'indigo' as const,
        module: null // Sempre visível
      }
    ]

    return allKPIs.filter(kpi => {
      // KPIs sem módulo específico sempre são exibidos
      if (!kpi.module) return true
      
      // Verifica se o módulo está habilitado
      return isModuleEnabled(kpi.module as any)
    })
  }

  const filteredKPIs = getFilteredKPIs()

  // Função para filtrar alertas baseado nos módulos habilitados
  const getFilteredAlerts = () => {
    return alerts.filter(alert => {
      // Alertas gerais sempre são exibidos
      if (alert.title.includes('Meta mensal') || alert.title.includes('receita')) {
        return true
      }
      
      // Alertas específicos de módulos
      if (alert.title.includes('CTE') || alert.message.includes('CTE') || alert.message.includes('transporte')) {
        return isModuleEnabled('cte')
      }
      
      if (alert.title.includes('cliente') || alert.message.includes('cliente') || alert.message.includes('carteira')) {
        return isModuleEnabled('clients')
      }
      
      if (alert.title.includes('CD') || alert.message.includes('CD') || alert.message.includes('Centro de Distribuição')) {
        return isModuleEnabled('cd')
      }
      
      if (alert.title.includes('NFe') || alert.message.includes('NFe') || alert.message.includes('nota fiscal')) {
        return isModuleEnabled('nfe')
      }
      
      // Por padrão, exibe alertas não categorizados
      return true
    })
  }

  const filteredAlerts = getFilteredAlerts()

  // Função para calcular o número de colunas baseado na quantidade de KPIs
  const getKPIGridCols = () => {
    const count = filteredKPIs.length
    
    if (count === 1) return 'grid-cols-1'
    if (count === 2) return 'grid-cols-1 md:grid-cols-2'
    if (count === 3) return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
    if (count === 4) return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
    if (count === 5) return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5'
    if (count === 6) return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6'
    
    // Para mais de 6 cards, usar grid responsivo máximo
    return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6'
  }

  return (
    <Page
      title="🚀 Dashboard Executivo"
      subtitle="Visão Geral Estratégica da Operação"
      onRefresh={() => {
        // Força refetch dos dados com os parâmetros atuais
        refetch()
      }}
      refreshing={isFetching}
    >
          {/* Filtro de Período */}
          <div className="mb-6">
            <PeriodFilter
              onFilter={handlePeriodFilter}
              isLoading={isFetching}
            />
          </div>

          {/* KPIs Row */}
          <div className={`grid ${getKPIGridCols()} gap-6`}>
            {filteredKPIs.map((kpi, index) => (
              <KPICard
                key={kpi.key}
                label={kpi.label}
                value={kpi.value}
                icon={kpi.icon}
                color={kpi.color}
                change={kpi.change}
                onClick={() => handleOpenModal(kpi.key)}
                animation={{
                  initial: { opacity: 0, y: 20 },
                  animate: { opacity: 1, y: 0 },
                  transition: { delay: 0.3 + index * 0.1 },
                  whileHover: { y: -5, scale: 1.02 }
                }}
              />
            ))}
          </div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Revenue Trend - mostrar apenas se nfe ou cte está ativo */}
            {(isModuleEnabled('nfe') || isModuleEnabled('cte')) && (
              <ChartCard
                title="Evolução da Receita"
                type="line"
                titleColor="green"
                data={{
                  labels: revenue_analytics.monthly_trend?.map(item => item.month) || [],
                  datasets: [{
                    label: 'Receita (R$)',
                    data: revenue_analytics.monthly_trend?.map(item => item.revenue) || [],
                    borderColor: 'rgb(75, 192, 192)',
                    backgroundColor: 'rgba(75, 192, 192, 0.2)',
                    tension: 0.1
                  }]
                }}
              />
            )}

            {/* CTE Status Distribution */}
            {isModuleEnabled('cte') && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
                className="rounded-2xl p-6 transition-colors duration-300"
                style={{ 
                  backgroundColor: 'var(--bg-card)',
                  border: '1px solid var(--border-primary)',
                  boxShadow: 'var(--shadow-md)'
                }}
              >
              <div className="flex items-center justify-between mb-6">
                <h3 
                  className="text-xl font-bold"
                  style={{ color: 'var(--text-primary)' }}
                >
                  Status dos CTEs
                </h3>
                <div className="flex items-center space-x-2" style={{ color: 'var(--text-secondary)' }}>
                  <PieChartIcon className="h-5 w-5" />
                  <span className="text-sm">Distribuição atual</span>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Entregues', value: cte_analytics.status_distribution?.completed, color: '#10b981' },
                      { name: 'Em Trânsito', value: cte_analytics.status_distribution?.in_transit, color: '#3b82f6' },
                      { name: 'Pendentes', value: cte_analytics.status_distribution?.pending, color: '#f59e0b' },
                      { name: 'Cancelados', value: cte_analytics.status_distribution?.cancelled, color: '#ef4444' }
                    ]}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={120}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {[
                      { name: 'Entregues', value: cte_analytics.status_distribution?.completed, color: '#10b981' },
                      { name: 'Em Trânsito', value: cte_analytics.status_distribution?.in_transit, color: '#3b82f6' },
                      { name: 'Pendentes', value: cte_analytics.status_distribution?.pending, color: '#f59e0b' },
                      { name: 'Cancelados', value: cte_analytics.status_distribution?.cancelled, color: '#ef4444' }
                    ].map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: 'var(--bg-card)',
                      border: '1px solid var(--border-primary)',
                      borderRadius: '12px',
                      color: 'var(--text-primary)',
                      boxShadow: 'var(--shadow-lg)'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-6 space-y-3">
                {[
                  { name: 'Entregues', value: cte_analytics.status_distribution?.completed, color: '#10b981' },
                  { name: 'Em Trânsito', value: cte_analytics.status_distribution?.in_transit, color: '#3b82f6' },
                  { name: 'Pendentes', value: cte_analytics.status_distribution?.pending, color: '#f59e0b' },
                  { name: 'Cancelados', value: cte_analytics.status_distribution?.cancelled, color: '#ef4444' }
                ].map((item, index) => (
                  <motion.div 
                    key={index} 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.6 + index * 0.1 }}
                    className="flex items-center justify-between p-3 rounded-xl transition-colors duration-300"
                    style={{ backgroundColor: 'var(--bg-secondary)' }}
                  >
                    <div className="flex items-center">
                      <div className="w-4 h-4 rounded-full mr-3" style={{ backgroundColor: item.color }} />
                      <span 
                        className="text-sm font-medium"
                        style={{ color: 'var(--text-primary)' }}
                      >
                        {item.name}
                      </span>
                    </div>
                    <span 
                      className="text-sm font-bold"
                      style={{ color: 'var(--text-primary)' }}
                    >
                      {item.value}
                    </span>
                  </motion.div>
                ))}
              </div>
              </motion.div>
            )}

            {/* Top Routes */}
            {isModuleEnabled('cte') && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="rounded-2xl p-6 transition-colors duration-300"
                style={{ 
                  backgroundColor: 'var(--bg-card)',
                  border: '1px solid var(--border-primary)',
                  boxShadow: 'var(--shadow-md)'
                }}
              >
              <div className="flex items-center justify-between mb-6">
                <h3 
                  className="text-lg font-bold"
                  style={{ color: 'var(--text-primary)' }}
                >
                  Principais Rotas
                </h3>
                <div className="flex items-center space-x-2" style={{ color: 'var(--text-secondary)' }}>
                  <Zap className="h-5 w-5" />
                  <span className="text-sm">Top 5</span>
                </div>
              </div>
              <div className="space-y-4">
                {cte_analytics.top_routes?.map((route, index) => (
                  <motion.div 
                    key={index} 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.8 + index * 0.1 }}
                    whileHover={{ scale: 1.02 }}
                    className="flex items-center justify-between p-4 rounded-xl transition-all duration-200"
                    style={{ backgroundColor: 'var(--bg-secondary)' }}
                  >
                    <div>
                      <p 
                        className="font-semibold"
                        style={{ color: 'var(--text-primary)' }}
                      >
                        {route.route}
                      </p>
                      <p 
                        className="text-sm"
                        style={{ color: 'var(--text-secondary)' }}
                      >
                        {route.volume} transportes
                      </p>
                    </div>
                    <div className="text-right">
                      <p 
                        className="font-bold"
                        style={{ color: 'var(--text-primary)' }}
                      >
                        R$ {formatNumber(route.revenue)}
                      </p>
                      <p 
                        className="text-sm font-medium"
                        style={{ color: 'var(--text-secondary)' }}
                      >
                        {route.efficiency}% eficiência
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
              </motion.div>
            )}

            {/* Performance Metrics - mostrar apenas se cte está ativo */}
            {isModuleEnabled('cte') && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9 }}
              className="rounded-2xl p-6 transition-colors duration-300"
              style={{
                backgroundColor: 'var(--bg-card)',
                border: '1px solid var(--border-primary)',
                boxShadow: 'var(--shadow-md)'
              }}
            >
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3
                    className="text-lg font-bold text-white px-4 py-3 rounded-lg"
                    style={{
                      background: 'linear-gradient(135deg, #3b82f6 0%, #0ea5e9 100%)'
                    }}
                  >
                    Performance de Entrega
                  </h3>
                  <div className="flex items-center space-x-2" style={{ color: 'var(--text-secondary)' }}>
                    <Target className="h-5 w-5" />
                    <span className="text-sm">Métricas</span>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span 
                    className="text-sm"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    Entregas no Prazo
                  </span>
                  <span 
                    className="font-bold"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    {((performance_metrics as any)?.delivery_performance?.on_time_delivery || 0).toFixed(1)}%
                  </span>
                </div>
                <div className="w-full rounded-full h-2" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
                  <motion.div 
                    initial={{ width: 0 }}
                    animate={{ width: `${((performance_metrics as any)?.delivery_performance?.on_time_delivery || 0)}%` }}
                    transition={{ delay: 1.0, duration: 1 }}
                    className="bg-gradient-to-r from-green-400 to-green-500 h-2 rounded-full"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span 
                    className="text-sm"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    Tempo Médio
                  </span>
                  <span 
                    className="font-bold"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    {((performance_metrics as any)?.delivery_performance?.average_delivery_time || 0).toFixed(1)} dias
                  </span>
                </div>
                <div className="w-full rounded-full h-2" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
                  <motion.div 
                    initial={{ width: 0 }}
                    animate={{ width: `${(((performance_metrics as any)?.delivery_performance?.average_delivery_time || 0) / 5 * 100).toFixed(1)}%` }}
                    transition={{ delay: 1.1, duration: 1 }}
                    className="bg-gradient-to-r from-blue-400 to-blue-500 h-2 rounded-full"
                  />
                </div>
              </div>
            </motion.div>
            )}
          </div>


          {/* Main Analytics Cards */}
          <div className="space-y-6 mt-8">
            {/* NFe Analytics Card */}
            {dashboardData?.nfe_analytics && isModuleEnabled('nfe') && (
              <MainSectionCard
                title="Análise de NFes"
                icon={FileText}
                color="blue"
                metrics={{
                  total: kpis?.total_nfes || 0,
                  unit: 'NFes'
                }}
                defaultOpen={true}
              >
                <NFeAnalyticsSection data={nfe_analytics as any || {
                  status_distribution: { authorized: dashboardData?.nfe_analytics.status_distribution?.authorized || 0, pending: dashboardData?.nfe_analytics.status_distribution?.pending || 0, cancelled: dashboardData?.nfe_analytics.status_distribution?.cancelled || 0, rejected: dashboardData?.nfe_analytics.status_distribution?.rejected || 0 },
                  monthly_volume: [],
                  top_senders: [],
                  nfe_by_state: [],
                  first_five_nfes_with_occurrence: (dashboardData?.nfe_analytics as any)?.first_five_nfes_with_occurrence || []
                }} />
              </MainSectionCard>
            )}

            {/* Transport Analysis Card */}
            {transport_analysis && isModuleEnabled('cte') && (
              <MainSectionCard
                title="Análise de Transportadoras"
                icon={Truck}
                color="green"
                metrics={{
                  total: (transport_analysis as any)?.carrier_comparison?.length || 0,
                  unit: 'Transportadoras'
                }}
                defaultOpen={true}
              >
                <TransportAnalysisSection data={transport_analysis as any || {
                  carrier_efficiency: {
                    most_efficient: { name: 'N/A', efficiency_score: 0, on_time_delivery: 0, cost_per_km: 0 },
                    least_efficient: { name: 'N/A', efficiency_score: 0, on_time_delivery: 0, cost_per_km: 0 }
                  },
                  freight_costs: {
                    most_expensive: { name: 'N/A', avg_cost_per_km: 0, volume: 0, total_cost: 0 },
                    cheapest: { name: 'N/A', avg_cost_per_km: 0, volume: 0, total_cost: 0 }
                  },
                  regional_analysis: { top_regions: [], cost_by_region: [] },
                  carrier_comparison: []
                }} />
              </MainSectionCard>
            )}

            {/* CTE Analytics Card */}
            {cte_analytics && Object.keys(cte_analytics).length > 0 && isModuleEnabled('cte') && (
              <MainSectionCard
                title="Análise de CTEs"
                icon={Package}
                color="purple"
                metrics={{
                  total: cte_analytics.status_distribution?.completed || 0,
                  unit: 'CTEs'
                }}
                defaultOpen={false}
              >
                <div className="space-y-6">
                  {/* CTE Status Overview */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div 
                      className="p-4 rounded-lg relative overflow-hidden"
                      style={{
                        backgroundColor: 'var(--bg-card)',
                        border: '1px solid var(--border-primary)',
                        boxShadow: 'var(--shadow-md)'
                      }}
                    >
                      <div className="relative flex items-center justify-between">
                        <div>
                          <p 
                            className="text-sm font-medium"
                            style={{ color: 'var(--text-secondary)' }}
                          >
                            Entregues
                          </p>
                          <p 
                            className="text-2xl font-bold"
                            style={{ color: 'var(--text-primary)' }}
                          >
                            {formatNumber(cte_analytics.status_distribution?.completed || 0)}
                          </p>
                        </div>
                        <div className="p-2 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 shadow-lg">
                          <CheckCircle className="h-6 w-6 text-white" />
                        </div>
                      </div>
                    </div>
                    
                    <div 
                      className="p-4 rounded-lg relative overflow-hidden"
                      style={{
                        backgroundColor: 'var(--bg-card)',
                        border: '1px solid var(--border-primary)',
                        boxShadow: 'var(--shadow-md)'
                      }}
                    >
                      <div className="relative flex items-center justify-between">
                        <div>
                          <p 
                            className="text-sm font-medium"
                            style={{ color: 'var(--text-secondary)' }}
                          >
                            Em Trânsito
                          </p>
                          <p 
                            className="text-2xl font-bold"
                            style={{ color: 'var(--text-primary)' }}
                          >
                            {formatNumber(cte_analytics.status_distribution?.in_transit || 0)}
                          </p>
                        </div>
                        <div className="p-2 rounded-xl bg-gradient-to-br from-yellow-500 to-yellow-600 shadow-lg">
                          <Truck className="h-6 w-6 text-white" />
                        </div>
                      </div>
                    </div>
                    
                    <div 
                      className="p-4 rounded-lg relative overflow-hidden"
                      style={{
                        backgroundColor: 'var(--bg-card)',
                        border: '1px solid var(--border-primary)',
                        boxShadow: 'var(--shadow-md)'
                      }}
                    >
                      <div className="relative flex items-center justify-between">
                        <div>
                          <p 
                            className="text-sm font-medium"
                            style={{ color: 'var(--text-secondary)' }}
                          >
                            Atrasados
                          </p>
                          <p 
                            className="text-2xl font-bold"
                            style={{ color: 'var(--text-primary)' }}
                          >
                            {formatNumber(cte_analytics.status_distribution?.cancelled || 0)}
                          </p>
                        </div>
                        <div className="p-2 rounded-xl bg-gradient-to-br from-red-500 to-red-600 shadow-lg">
                          <AlertTriangle className="h-6 w-6 text-white" />
                        </div>
                      </div>
                    </div>
                    
                    <div 
                      className="p-4 rounded-lg relative overflow-hidden"
                      style={{
                        backgroundColor: 'var(--bg-card)',
                        border: '1px solid var(--border-primary)',
                        boxShadow: 'var(--shadow-md)'
                      }}
                    >
                      <div className="relative flex items-center justify-between">
                        <div>
                          <p 
                            className="text-sm font-medium"
                            style={{ color: 'var(--text-secondary)' }}
                          >
                            Pendentes
                          </p>
                          <p 
                            className="text-2xl font-bold"
                            style={{ color: 'var(--text-primary)' }}
                          >
                            {formatNumber(cte_analytics.status_distribution?.pending || 0)}
                          </p>
                        </div>
                        <div className="p-2 rounded-xl bg-gradient-to-br from-gray-500 to-gray-600 shadow-lg">
                          <Clock className="h-6 w-6 text-white" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* CTE Monthly Trend */}
                  <div className="rounded-xl p-6 shadow-lg border"
                    style={{
                      backgroundColor: 'var(--bg-card)',
                      borderColor: 'var(--border-primary)'
                    }}>
                    <h3 
                      className="text-xl font-semibold mb-6 flex items-center"
                      style={{ color: 'var(--text-primary)' }}
                    >
                      <LineChart 
                        className="h-5 w-5 mr-2" 
                        style={{ color: 'var(--primary)' }}
                      />
                      Tendência Mensal de CTEs
                    </h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <ComposedChart data={cte_analytics.monthly_volume || []}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip 
                          contentStyle={{
                            backgroundColor: 'var(--bg-card)',
                            border: '1px solid var(--border-primary)',
                            borderRadius: '8px'
                          }}
                        />
                        <Area 
                          type="monotone" 
                          dataKey="volume" 
                          fill="#8b5cf6" 
                          fillOpacity={0.3}
                          stroke="#8b5cf6"
                        />
                        <Line 
                          type="monotone" 
                          dataKey="volume" 
                          stroke="#8b5cf6" 
                          strokeWidth={3}
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </MainSectionCard>
            )}

            {/* Distribution Centers Card */}
            {dashboardData?.cd_analytics && isModuleEnabled('cd') && (
              <MainSectionCard
                title="Centros de Distribuição"
                icon={Warehouse}
                color="orange"
                metrics={{
                  total: (cd_analytics as any)?.cds_by_status?.find((s: any) => s.status === 'Ativo')?.count || 0,
                  unit: 'CDs Ativos'
                }}
                defaultOpen={false}
              >
                <div className="space-y-6">
                  {/* CD Status */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {(cd_analytics as any)?.cds_by_status?.map((status: any, index: number) => (
                      <div key={index} className="rounded-lg p-4 shadow border"
                        style={{
                          backgroundColor: 'var(--bg-card)',
                          borderColor: 'var(--border-primary)'
                        }}>
                        <div className="flex items-center justify-between">
                          <div>
                            <p 
                              className="text-sm font-medium"
                              style={{ color: 'var(--text-primary)' }}
                            >
                              {status.status}
                            </p>
                            <p 
                              className="text-2xl font-bold"
                              style={{ color: 'var(--text-primary)' }}
                            >
                              {status.count}
                            </p>
                            <p 
                              className="text-xs"
                              style={{ color: 'var(--text-tertiary)' }}
                            >
                              {status.percentage.toFixed(1)}%
                            </p>
                          </div>
                          <div 
                            className="h-8 w-8 rounded-full flex items-center justify-center"
                            style={{
                              backgroundColor: 'var(--bg-tertiary)',
                              opacity: 0.8
                            }}
                          >
                            <Building2 
                              className="h-4 w-4" 
                              style={{ color: 'var(--primary)' }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* CD Performance */}
                  <div className="rounded-xl p-6 shadow-lg border"
                    style={{
                      backgroundColor: 'var(--bg-card)',
                      borderColor: 'var(--border-primary)'
                    }}>
                    <h3 
                      className="text-xl font-semibold mb-6 flex items-center"
                      style={{ color: 'var(--text-primary)' }}
                    >
                      <BarChart3 
                        className="h-5 w-5 mr-2" 
                        style={{ color: 'var(--primary)' }}
                      />
                      Performance dos CDs
                    </h3>
                    <div className="space-y-4">
                      {(cd_analytics as any)?.cd_performance?.map((cd: any, index: number) => (
                        <div 
                          key={index} 
                          className="flex items-center justify-between p-4 rounded-lg"
                          style={{
                            backgroundColor: 'var(--bg-tertiary)',
                            opacity: 0.6
                          }}
                        >
                          <div>
                            <h4 
                              className="font-semibold"
                              style={{ color: 'var(--text-primary)' }}
                            >
                              {cd.cd}
                            </h4>
                            <p 
                              className="text-sm"
                              style={{ color: 'var(--text-primary)' }}
                            >
                              Throughput: {formatNumber(cd.throughput)} unidades
                            </p>
                          </div>
                          <div className="text-right">
                            <div 
                              className="text-lg font-bold"
                              style={{ color: 'var(--text-primary)' }}
                            >
                              {cd.efficiency.toFixed(1)}%
                            </div>
                            <div 
                              className="text-sm"
                              style={{ color: 'var(--text-secondary)' }}
                            >
                              R$ {cd.cost_per_unit.toFixed(2)}/unidade
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </MainSectionCard>
            )}
          </div>

          {/* Logistics Network and Client Analytics - Side by Side */}
          {(dashboardData?.logistics_analytics && isModuleEnabled('logistics')) || (dashboardData?.client_analytics && isModuleEnabled('clients')) ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Logistics Network Card */}
                {dashboardData?.logistics_analytics && isModuleEnabled('logistics') && (
              <ClickableCardModal
                title="Malha Logística"
                icon={Network}
                color="indigo"
                titleColor="indigo"
                description="Cobertura e Performance da Rede"
                metrics={{
                  total: (logistics_network_modal_data as any)?.geographic_distribution?.top_regions?.length || 0,
                  unit: 'Regiões Atendidas'
                }}
                onClick={() => setIsLogisticsModalOpen(true)}
              />
                )}

                {/* Client Analytics Card */}
                {dashboardData?.client_analytics && isModuleEnabled('clients') && (
              <ClickableCardModal
                title="Carteira de Clientes"
                icon={UserCheck}
                color="red"
                titleColor="red"
                description="Segmentação, Risco e Oportunidades"
                metrics={{
                  total: (client_analytics as any)?.portfolio_health?.total_clients || 0,
                  unit: 'Clientes'
                }}
                onClick={() => setIsClientModalOpen(true)}
              />
                )}
              </div>
            ) : null}

          {/* Alerts Section - Alertas Inteligentes com IA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2 }}
            className="rounded-2xl p-6 transition-colors duration-300"
            style={{
              backgroundColor: 'var(--bg-card)',
              border: '1px solid var(--border-primary)',
              boxShadow: 'var(--shadow-md)'
            }}
          >
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <div
                  className="flex items-center space-x-3 text-white px-4 py-3 rounded-lg font-bold text-xl"
                  style={{
                    background: 'linear-gradient(135deg, #ef4444 0%, #f97316 100%)'
                  }}
                >
                  <Bell className="h-6 w-6" />
                  <span>🚨 Alertas Inteligentes</span>
                </div>
                <div className="flex items-center space-x-2 px-3 py-1 rounded-full" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
                  <span className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                    {filteredAlerts.length} alerta{filteredAlerts.length !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>
            </div>

            {filteredAlerts.length === 0 ? (
              <div className="flex items-center justify-center space-x-2 py-8">
                <CheckCircle className="h-8 w-8 text-green-500" />
                <span className="text-lg font-medium" style={{ color: 'var(--text-secondary)' }}>
                  Nenhum alerta no momento. Tudo funcionando perfeitamente! 🎉
                </span>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredAlerts.map((alert: any, index: number) => {
                  // Determinar cores baseado no tipo
                  const getAlertColors = (type: string) => {
                    switch (type) {
                      case 'success':
                        return { bg: '#10b98120', border: '#10b981', icon: '#10b981' }
                      case 'warning':
                        return { bg: '#f59e0b20', border: '#f59e0b', icon: '#f59e0b' }
                      case 'danger':
                        return { bg: '#ef444420', border: '#ef4444', icon: '#ef4444' }
                      case 'info':
                      default:
                        return { bg: '#3b82f620', border: '#3b82f6', icon: '#3b82f6' }
                    }
                  }

                  const getPriorityBadge = (priority: string) => {
                    const colors = {
                      high: { bg: '#ef444420', text: '#ef4444', label: 'Alta' },
                      medium: { bg: '#f59e0b20', text: '#f59e0b', label: 'Média' },
                      low: { bg: '#10b98120', text: '#10b981', label: 'Baixa' }
                    }
                    const config = colors[priority as keyof typeof colors] || colors.medium
                    return (
                      <span
                        className="px-2 py-1 rounded-full text-xs font-semibold"
                        style={{ backgroundColor: config.bg, color: config.text }}
                      >
                        {config.label}
                      </span>
                    )
                  }

                  const colors = getAlertColors(alert.type)
                  const AlertIcon = alert.type === 'success' ? CheckCircle :
                                   alert.type === 'warning' ? AlertTriangle :
                                   alert.type === 'danger' ? AlertCircle :
                                   Activity

                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 1.3 + index * 0.1 }}
                      className="p-4 rounded-xl transition-all duration-300"
                      style={{
                        backgroundColor: colors.bg,
                        border: `1px solid ${colors.border}`,
                        boxShadow: 'var(--shadow-sm)'
                      }}
                    >
                      <div className="flex items-start space-x-3">
                        {/* Icon */}
                        <div className="flex-shrink-0 mt-1">
                          <AlertIcon className="h-5 w-5" style={{ color: colors.icon }} />
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          {/* Header com prioridade */}
                          <div className="flex items-center justify-between mb-2">
                            <p
                              className="font-bold text-sm"
                              style={{ color: 'var(--text-primary)' }}
                            >
                              {alert.title}
                            </p>
                            {alert.priority && getPriorityBadge(alert.priority)}
                          </div>

                          {/* Message */}
                          <p
                            className="text-xs mb-2"
                            style={{ color: 'var(--text-secondary)' }}
                          >
                            {alert.message}
                          </p>

                          {/* Valor/Métrica com savings */}
                          {(alert.value !== undefined || alert.potential_savings) && (
                            <div className="flex items-center space-x-2 mb-2">
                              {alert.value !== undefined && (
                                <div className="flex items-center space-x-1">
                                  <span className="text-xs font-semibold" style={{ color: colors.icon }}>
                                    {alert.value > 0 ? '+' : ''}{alert.value.toFixed(1)}%
                                  </span>
                                </div>
                              )}
                              {alert.potential_savings && (
                                <span className="text-xs px-2 py-0.5 rounded-full" style={{
                                  backgroundColor: '#10b98120',
                                  color: '#10b981'
                                }}>
                                  💰 {alert.potential_savings}
                                </span>
                              )}
                            </div>
                          )}

                          {/* Action Required */}
                          {alert.action_required && (
                            <div className="mt-2 p-2 rounded-lg" style={{
                              backgroundColor: 'var(--bg-card)',
                              border: '1px solid var(--border-primary)'
                            }}>
                              <div className="text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>
                                💡 Ação
                              </div>
                              <div className="text-xs" style={{ color: 'var(--text-primary)' }}>
                                {alert.action_required}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  )
                })}
              </div>
            )}

            {/* Footer */}
            {filteredAlerts.length > 0 && (
              <div className="mt-4 p-3 rounded-lg" style={{
                backgroundColor: 'var(--bg-tertiary)',
                border: '1px solid var(--border-primary)'
              }}>
                <div className="flex items-center justify-between text-xs">
                  <span style={{ color: 'var(--text-secondary)' }}>
                    <span className="font-semibold">💡 IA:</span> Alertas gerados automaticamente com base em análise de dados reais
                  </span>
                  <span style={{ color: 'var(--text-secondary)' }}>
                    Atualizado em tempo real
                  </span>
                </div>
              </div>
            )}
          </motion.div>

          {/* Modais */}
          {dashboardData && (
            <>
              {/* Revenue Modal */}
              <Modal
                isOpen={openModal === 'total_revenue'}
                onClose={handleCloseModal}
                title="Análise Detalhada de Receita"
                icon={DollarSign}
                color="green"
                lastUpdated="Hoje às 14:30"
                dataSource="Sistema de Faturamento"
              >
                <RevenueModal data={{
                total_revenue: kpis.total_revenue || 0,
                monthly_growth: kpis.monthly_growth || 0,
                revenue_analytics: {
                  monthly_revenue: revenue_analytics.monthly_trend?.map(item => ({
                    month: item.month,
                    revenue: item.revenue,
                    growth: item.growth
                  })) || [],
                  revenue_by_segment: revenue_analytics.revenue_by_service?.map(item => ({
                    segment: item.service,
                    revenue: item.revenue,
                    percentage: item.percentage
                  })) || [],
                  top_clients: revenue_analytics.revenue_by_region?.map(item => ({
                    client: item.region,
                    revenue: item.revenue,
                    percentage: item.percentage
                  })) || []
                }
              }} />
              </Modal>

              {/* CTE Modal */}
              <Modal
                isOpen={openModal === 'total_ctes'}
                onClose={handleCloseModal}
                title="Análise Detalhada de CTEs"
                icon={Truck}
                color="blue"
                lastUpdated="Hoje às 14:30"
                dataSource="Sistema de Transporte"
              >
                <CTEModal data={{
                total_ctes: kpis.total_ctes || 0,
                cte_analytics: {
                  status_distribution: {
                    completed: cte_analytics?.status_distribution?.completed || 0,
                    in_transit: cte_analytics?.status_distribution?.in_transit || 0,
                    pending: cte_analytics?.status_distribution?.pending || 0,
                    cancelled: cte_analytics?.status_distribution?.cancelled || 0
                  },
                  monthly_volume: cte_analytics?.monthly_volume?.map(item => ({
                    month: item.month,
                    volume: item.volume,
                    efficiency: item.value || 85 // Valor padrão para eficiência
                  })) || [],
                  top_routes: cte_analytics?.top_routes?.map(item => ({
                    route: item.route,
                    volume: item.volume,
                    efficiency: item.efficiency,
                    avg_time: item.distance || 5 // Valor padrão para tempo médio
                  })) || [],
                  cte_by_type: cte_analytics?.cte_by_type || []
                }
              }} />
              </Modal>

              {/* NFE Modal */}
              <Modal
                isOpen={openModal === 'total_nfes'}
                onClose={handleCloseModal}
                title="Análise Detalhada de NFes"
                icon={FileText}
                color="purple"
                lastUpdated="Hoje às 14:30"
                dataSource="Sistema Fiscal"
              >
                <NFEModal data={{
                total_nfes: kpis.total_nfes || 0,
                nfe_analytics: {
                  status_distribution: {
                    authorized: nfe_analytics?.status_distribution?.authorized || 0,
                    pending: nfe_analytics?.status_distribution?.pending || 0,
                    cancelled: nfe_analytics?.status_distribution?.cancelled || 0,
                    rejected: nfe_analytics?.status_distribution?.rejected || 0
                  },
                  monthly_volume: nfe_analytics?.monthly_volume || [],
                  top_senders: nfe_analytics?.top_senders || [],
                  nfe_by_state: nfe_analytics?.nfe_by_state || []
                }
              }} />
              </Modal>

              {/* CD Modal */}
              <Modal
                isOpen={openModal === 'active_cds'}
                onClose={handleCloseModal}
                title="Análise Detalhada dos CDs"
                icon={Building2}
                color="orange"
                lastUpdated="Hoje às 14:30"
                dataSource="Sistema WMS"
              >
                <CDModal data={{
                active_cds: kpis.active_cds || 0,
                cd_analytics: {
                  cds_by_status: (cd_analytics as any)?.cds_by_status || [],
                  cds_by_region: (cd_analytics as any)?.cds_by_region || [],
                  cd_performance: (cd_analytics as any)?.cd_performance || []
                }
              }} />
              </Modal>

              {/* Client Modal */}
              <Modal
                isOpen={openModal === 'total_clients'}
                onClose={handleCloseModal}
                title="Análise Detalhada de Clientes"
                icon={Users}
                color="pink"
                lastUpdated="Hoje às 14:30"
                dataSource="Sistema CRM"
              >
                <ClientModal data={{
                total_clients: kpis.total_clients || 0,
                client_analytics: {
                  portfolio_health: (client_analytics as any)?.portfolio_health || {},
                  segmentation: (client_analytics as any)?.segmentation || {},
                  risk_analysis: (client_analytics as any)?.risk_analysis || {},
                  geographic_distribution: (client_analytics as any)?.geographic_distribution || {}
                }
              }} />
              </Modal>

              {/* Efficiency Modal */}
              <Modal
                isOpen={openModal === 'efficiency_score'}
                onClose={handleCloseModal}
                title="Análise Detalhada de Eficiência"
                icon={Target}
                color="indigo"
                lastUpdated="Hoje às 14:30"
                dataSource="Múltiplas Fontes"
              >
                <EfficiencyModal data={{
                efficiency_score: kpis.efficiency_score || 0,
                performance_metrics: {
                  on_time_delivery: (performance_metrics as any)?.delivery_performance?.on_time_delivery || 0,
                  cost_efficiency: (performance_metrics as any)?.operational_efficiency?.fuel_efficiency || 0,
                  customer_satisfaction: (performance_metrics as any)?.quality_metrics?.customer_retention || 0,
                  operational_efficiency: (performance_metrics as any)?.operational_efficiency?.vehicle_utilization || 0
                },
                strategic_insights: (strategic_insights as any)?.growth_opportunities?.map((opp: any) => ({
                  insight: opp.opportunity || 'Insight não disponível',
                  impact: `Potencial de receita: R$ ${(opp.potential_revenue || 0).toLocaleString()}`,
                  recommendation: `ROI: ${opp.roi || 0}%`
                })) || []
              }} />
              </Modal>
            </>
          )}

          {/* Logistics Network Modal */}
          <LogisticsNetworkModal
            isOpen={isLogisticsModalOpen}
            onClose={() => setIsLogisticsModalOpen(false)}
            data={logistics_network_modal_data}
            onPeriodChange={handleLogisticsPeriodChange}
            isLoading={isLoadingLogistics}
          />

          {/* Client Portfolio Modal */}
          <ClientPortfolioModal
            isOpen={isClientModalOpen}
            onClose={() => setIsClientModalOpen(false)}
            data={client_analytics}
          />

          {/* ✨ NOVOS COMPONENTES COM IA AVANÇADA ✨ */}

          {/* Insights Estratégicos estão integrados nos cards de Carteira e Logística */}
        </Page>
      )
}

export default ExecutiveDashboard